import React, { useState, useEffect } from "react";
import PageShell from "../../components/layout/PageShell";
import GlassCard from "../../components/ui/GlassCard";
import StatCard from "../../components/ui/StatCard";
import Table, { Column } from "../../components/ui/Table";
import Modal from "../../components/ui/Modal";
import ConfirmDialog from "../../components/ui/ConfirmDialog";
import Button from "../../components/ui/Button";
import Badge from "../../components/ui/Badge";
import { Input, Select, Textarea } from "../../components/ui/FormFields";
import {
  collection,
  getDocs,
  addDoc,
  doc,
  updateDoc,
  deleteDoc,
  query,
  where,
  getDoc,
  writeBatch
} from "firebase/firestore";
import {
  createUserWithEmailAndPassword,
  updatePassword,
  signInWithEmailAndPassword,
  deleteUser
} from "firebase/auth";
import { db, auth } from "../../firebase/config";
import * as XLSX from "xlsx";
import Swal from "sweetalert2";

interface Candidate {
  id: string;
  email: string;
  name: string;
  role: string | null;
  createdAt: string;
  isActive: boolean;
  authUid?: string;
  orgId?: string | null;
  status?: string;
  updatedAt?: string;
  hasPassword?: boolean;
}

interface Group {
  id: string;
  name: string;
  description: string;
  orgId: string;
  candidateIds: string[];
  createdAt: string;
  updatedAt: string;
  createdBy: string;
  candidateCount?: number;
}

interface Props {
  orgId?: string;
  onCandidateChange?: () => void;
}

const DEFAULT_PASSWORD = "123456";

const BLANK = {
  name: "",
  email: "",
  status: "active" as string,
  password: DEFAULT_PASSWORD
};

const LoadingOverlay: React.FC<{ message?: string; progress?: number; showProgress?: boolean }> = ({
  message = "Processing...",
  progress = 0,
  showProgress = false
}) => (
  <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center">
    <div className="bg-white rounded-lg p-6 w-96 shadow-xl">
      <div className="flex flex-col items-center mb-4">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mb-4"></div>
        <h3 className="text-lg font-semibold text-gray-900">{message}</h3>
      </div>
      {showProgress && (
        <>
          <div className="w-full bg-gray-200 rounded-full h-2.5 mb-2">
            <div
              className="bg-indigo-600 h-2.5 rounded-full transition-all duration-300 ease-in-out"
              style={{ width: `${progress}%` }}
            ></div>
          </div>
          <p className="text-sm text-gray-600 text-center">{Math.round(progress)}% complete</p>
        </>
      )}
    </div>
  </div>
);

const ProgressModal: React.FC<{
  isOpen: boolean;
  progress: number;
  message: string;
  details?: string;
}> = ({ isOpen, progress, message, details }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center">
      <div className="bg-white rounded-lg p-6 w-96 shadow-xl">
        <div className="mb-4">
          <h3 className="text-lg font-semibold text-gray-900">{message}</h3>
          {details && <p className="text-sm text-gray-500 mt-1">{details}</p>}
        </div>
        <div className="w-full bg-gray-200 rounded-full h-2.5 mb-4">
          <div
            className="bg-indigo-600 h-2.5 rounded-full transition-all duration-300 ease-in-out"
            style={{ width: `${progress}%` }}
          ></div>
        </div>
        <div className="text-right">
          <span className="text-sm font-medium text-gray-700">{Math.round(progress)}%</span>
        </div>
      </div>
    </div>
  );
};

const CandidatesPage: React.FC<Props> = ({ orgId, onCandidateChange }) => {
  const [candidates, setCandidates] = useState<Candidate[]>([]);
  const [groups, setGroups] = useState<Group[]>([]);
  const [search, setSearch] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [editC, setEditC] = useState<Candidate | null>(null);
  const [deleteC, setDeleteC] = useState<Candidate | null>(null);
  const [form, setForm] = useState(BLANK);
  const [loading, setLoading] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState("Loading...");
  const [importing, setImporting] = useState(false);
  const [currentOrgId, setCurrentOrgId] = useState<string | null>(null);
  const [progress, setProgress] = useState(0);
  const [progressMessage, setProgressMessage] = useState("");
  const [progressDetails, setProgressDetails] = useState("");
  const [showProgress, setShowProgress] = useState(false);
  const [selectedCandidates, setSelectedCandidates] = useState<Set<string>>(new Set());
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [activeTab, setActiveTab] = useState<"candidates" | "groups">("candidates");
  const [showGroupModal, setShowGroupModal] = useState(false);
  const [editGroup, setEditGroup] = useState<Group | null>(null);
  const [deleteGroup, setDeleteGroup] = useState<Group | null>(null);
  const [groupForm, setGroupForm] = useState({ name: "", description: "" });
  const [selectedGroup, setSelectedGroup] = useState<Group | null>(null);
  const [showAddCandidatesModal, setShowAddCandidatesModal] = useState(false);
  const [selectedCandidatesForGroup, setSelectedCandidatesForGroup] = useState<Set<string>>(new Set());
  const [groupCandidates, setGroupCandidates] = useState<Candidate[]>([]);
  const [loadingGroupCandidates, setLoadingGroupCandidates] = useState(false);

  useEffect(() => {
    loadCurrentOrg();
  }, []);

  useEffect(() => {
    if (currentOrgId) {
      loadCandidates();
      loadGroups();
    }
  }, [currentOrgId, orgId]);

  const loadCurrentOrg = async () => {
    setLoading(true);
    setLoadingMessage("Loading organisation details...");
    try {
      const currentUser = auth.currentUser;
      if (currentUser) {
        const usersRef = collection(db, "users");
        const q = query(usersRef, where("authUid", "==", currentUser.uid));
        const snapshot = await getDocs(q);

        if (!snapshot.empty) {
          const userData = snapshot.docs[0].data();
          if (userData.orgId) {
            setCurrentOrgId(userData.orgId);
          }
        }
      }
    } catch (error) {
      console.error("Error loading current org:", error);
      await Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Failed to load organisation details'
      });
    } finally {
      setLoading(false);
    }
  };

  const loadCandidates = async () => {
    setLoading(true);
    setLoadingMessage("Loading candidates...");
    try {
      const orgIdToUse = orgId || currentOrgId;
      if (!orgIdToUse) {
        setCandidates([]);
        return;
      }

      const usersRef = collection(db, "users");
      const q = query(
        usersRef,
        where("orgId", "==", orgIdToUse),
        where("role", "==", "candidate")
      );
      const querySnapshot = await getDocs(q);
      const loadedCandidates: Candidate[] = [];

      querySnapshot.forEach((doc) => {
        loadedCandidates.push({ id: doc.id, ...doc.data() } as Candidate);
      });

      setCandidates(loadedCandidates);
      setSelectedCandidates(new Set());
    } catch (error) {
      console.error("Error loading candidates:", error);
      await Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Failed to load candidates'
      });
    } finally {
      setLoading(false);
    }
  };

  const loadGroups = async () => {
    try {
      const orgIdToUse = orgId || currentOrgId;
      if (!orgIdToUse) return;

      const groupsRef = collection(db, "groups");
      const q = query(groupsRef, where("orgId", "==", orgIdToUse));
      const querySnapshot = await getDocs(q);
      const loadedGroups: Group[] = [];

      querySnapshot.forEach((doc) => {
        const group = { id: doc.id, ...doc.data() } as Group;
        group.candidateCount = group.candidateIds?.length || 0;
        loadedGroups.push(group);
      });

      setGroups(loadedGroups.sort((a, b) => a.name.localeCompare(b.name)));
    } catch (error) {
      console.error("Error loading groups:", error);
    }
  };

  const F = (k: keyof typeof BLANK) => (v: string) => setForm(f => ({ ...f, [k]: v }));

  const openNew = () => {
    setEditC(null);
    setForm(BLANK);
    setShowModal(true);
  };

  const openEdit = (u: Candidate) => {
    setEditC(u);
    setForm({
      name: u.name,
      email: u.email,
      status: u.status || "active",
      password: ""
    });
    setShowModal(true);
  };

  const save = async () => {
    if (!form.name.trim() || !form.email.trim()) return;
    setLoading(true);
    setLoadingMessage(editC ? "Updating candidate..." : "Creating candidate...");

    try {
      const orgIdToUse = orgId || currentOrgId;
      if (!orgIdToUse) throw new Error("No organisation selected");

      if (editC) {
        const userRef = doc(db, "users", editC.id);
        await updateDoc(userRef, {
          name: form.name,
          email: form.email,
          status: form.status,
          updatedAt: new Date().toISOString()
        });

        if (form.password && form.password.trim()) {
          setLoadingMessage("Updating password...");
          try {
            const userCredential = await signInWithEmailAndPassword(auth, editC.email, DEFAULT_PASSWORD);
            await updatePassword(userCredential.user, form.password);
            await auth.signOut();
          } catch (authError) {
            console.log("Could not update password, user may need to reset");
          }
        }

        await loadCandidates();
        await Swal.fire({
          icon: 'success',
          title: 'Updated!',
          text: 'Candidate updated successfully',
          timer: 1500,
          showConfirmButton: false
        });
      } else {
        const usersRef = collection(db, "users");
        const emailQuery = query(usersRef, where("email", "==", form.email));
        const emailSnapshot = await getDocs(emailQuery);

        if (!emailSnapshot.empty) {
          await Swal.fire({
            icon: 'error',
            title: 'Email Exists',
            text: 'Email already exists!'
          });
          setLoading(false);
          return;
        }

        setLoadingMessage("Creating authentication account...");
        const userCredential = await createUserWithEmailAndPassword(auth, form.email, form.password);

        setLoadingMessage("Saving candidate data...");
        const newCandidate = {
          email: form.email,
          name: form.name,
          role: "candidate",
          status: form.status,
          isActive: true,
          hasPassword: true,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
          authUid: userCredential.user.uid,
          orgId: orgIdToUse,
        };

        await addDoc(usersRef, newCandidate);
        await loadCandidates();

        await Swal.fire({
          icon: 'success',
          title: 'Added!',
          text: 'Candidate added successfully',
          timer: 1500,
          showConfirmButton: false
        });

        if (onCandidateChange) onCandidateChange();
      }

      setShowModal(false);
    } catch (error: any) {
      console.error("Error saving candidate:", error);
      await Swal.fire({
        icon: 'error',
        title: 'Error',
        text: error.message || "Error saving candidate"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSingleDelete = async () => {
    if (!deleteC || !deleteC.id) return;

    const result = await Swal.fire({
      title: 'Delete Candidate?',
      text: `Are you sure you want to delete "${deleteC.name}"? This action cannot be undone.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#ef4444',
      cancelButtonColor: '#6b7280',
      confirmButtonText: 'Yes, delete!',
      cancelButtonText: 'Cancel'
    });

    if (!result.isConfirmed) {
      setDeleteC(null);
      return;
    }

    setLoading(true);
    setLoadingMessage(`Deleting candidate "${deleteC.name}"...`);

    try {
      setLoadingMessage("Removing from database...");
      const userRef = doc(db, "users", deleteC.id);
      await deleteDoc(userRef);

      setLoadingMessage("Removing authentication account...");
      try {
        const userCredential = await signInWithEmailAndPassword(auth, deleteC.email, DEFAULT_PASSWORD);
        await deleteUser(userCredential.user);
        await auth.signOut();
      } catch (authError: any) {
        console.log("Auth user deletion error:", authError.message);
        if (authError.code !== 'auth/user-not-found') {
          console.warn("Could not delete auth user:", authError);
        }
      }

      await loadCandidates();
      setDeleteC(null);

      await Swal.fire({
        icon: 'success',
        title: 'Deleted!',
        text: 'Candidate removed successfully',
        timer: 1500,
        showConfirmButton: false
      });

      if (onCandidateChange) onCandidateChange();
    } catch (error: any) {
      console.error("Error deleting candidate:", error);
      await Swal.fire({
        icon: 'error',
        title: 'Error',
        text: error.message || "Error deleting candidate"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleBulkDeleteSelected = async () => {
    if (selectedCandidates.size === 0) return;

    const result = await Swal.fire({
      title: 'Delete Selected Candidates?',
      text: `Delete ${selectedCandidates.size} candidates? This action cannot be undone.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#ef4444',
      cancelButtonText: 'Cancel',
      confirmButtonText: 'Yes, delete all'
    });

    if (!result.isConfirmed) return;

    setBulkDeleting(true);
    setShowProgress(true);
    setProgress(0);
    setProgressMessage("Preparing to delete candidates...");

    try {
      const ids = Array.from(selectedCandidates);
      let deletedCount = 0;
      let errorCount = 0;

      for (let i = 0; i < ids.length; i++) {
        const id = ids[i];
        const candidate = candidates.find(c => c.id === id);

        if (!candidate) {
          errorCount++;
          continue;
        }

        const percentComplete = ((i + 1) / ids.length) * 100;
        setProgress(percentComplete);
        setProgressMessage(`Deleting candidate ${i + 1} of ${ids.length}`);
        setProgressDetails(`Currently deleting: ${candidate.name}`);

        try {
          await deleteDoc(doc(db, "users", id));

          try {
            const userCredential = await signInWithEmailAndPassword(auth, candidate.email, DEFAULT_PASSWORD);
            await deleteUser(userCredential.user);
            await auth.signOut();
          } catch (authError) {
          }

          deletedCount++;
        } catch (error) {
          errorCount++;
          console.error(`Failed to delete candidate ${candidate.name}:`, error);
        }

        await new Promise(resolve => setTimeout(resolve, 100));
      }

      setProgress(100);
      setProgressMessage("Cleaning up...");
      await new Promise(resolve => setTimeout(resolve, 500));

      setSelectedCandidates(new Set());
      await loadCandidates();
      setShowProgress(false);

      if (deletedCount > 0 && errorCount === 0) {
        await Swal.fire({
          icon: 'success',
          title: 'Bulk Delete Complete!',
          html: `
            <div class="text-left">
              <p><strong>Successfully Deleted:</strong> ${deletedCount}</p>
              <p><strong>Failed:</strong> ${errorCount}</p>
            </div>
          `,
          confirmButtonColor: '#ef4444'
        });
      } else if (deletedCount > 0 && errorCount > 0) {
        await Swal.fire({
          icon: 'warning',
          title: 'Partial Delete',
          html: `
            <div class="text-left">
              <p><strong>Successfully Deleted:</strong> ${deletedCount}</p>
              <p><strong>Failed:</strong> ${errorCount}</p>
              <p class="text-sm text-gray-500 mt-2">Some candidates could not be deleted. They may have been already removed.</p>
            </div>
          `,
          confirmButtonColor: '#ef4444'
        });
      } else {
        await Swal.fire({
          icon: 'error',
          title: 'Delete Failed',
          text: 'Failed to delete selected candidates',
          confirmButtonColor: '#ef4444'
        });
      }

    } catch (error) {
      console.error(error);
      setShowProgress(false);
      await Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'An error occurred during bulk delete'
      });
    }

    setBulkDeleting(false);
  };

  const handleSelectAll = () => {
    if (isAllSelected) {
      setSelectedCandidates(new Set());
    } else {
      const allIds = filtered.map(c => c.id);
      setSelectedCandidates(new Set(allIds));
    }
  };

  const handleSelectCandidate = (id: string) => {
    setSelectedCandidates(prev => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      return newSet;
    });
  };

  const downloadTemplate = () => {
    const template = [
      { name: "John Doe", email: "john.doe@example.com", status: "active" },
      { name: "Jane Smith", email: "jane.smith@example.com", status: "active" }
    ];

    const ws = XLSX.utils.json_to_sheet(template);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Candidates_Template");
    XLSX.writeFile(wb, `candidates_template_${new Date().toISOString().split("T")[0]}.xlsx`);

    Swal.fire({
      icon: 'success',
      title: 'Template Downloaded!',
      text: 'Fill in the template with your candidate data',
      timer: 2000,
      showConfirmButton: false
    });
  };

  const handleBulkImport = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setImporting(true);
    setShowProgress(true);
    setProgress(0);
    setProgressMessage("Reading file...");
    setProgressDetails("");

    const reader = new FileReader();

    reader.onload = async (e) => {
      try {
        const data = new Uint8Array(e.target?.result as ArrayBuffer);
        setProgressMessage("Parsing Excel file...");
        setProgress(10);

        const workbook = XLSX.read(data, { type: 'array' });
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];
        const importedData = XLSX.utils.sheet_to_json(worksheet);

        const orgIdToUse = orgId || currentOrgId;
        if (!orgIdToUse) throw new Error("No organisation selected");

        const totalRecords = importedData.length;
        if (totalRecords === 0) {
          throw new Error("No data found in file");
        }

        if (totalRecords > 500) {
          const confirm = await Swal.fire({
            title: 'Large Import',
            text: `You are about to import ${totalRecords} candidates. This may take several minutes. Do you want to continue?`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Yes, continue',
            cancelButtonText: 'Cancel'
          });

          if (!confirm.isConfirmed) {
            setShowProgress(false);
            setImporting(false);
            event.target.value = "";
            return;
          }
        }

        setProgressMessage(`Processing ${totalRecords} candidates...`);

        let successCount = 0;
        let errorCount = 0;
        const errors: string[] = [];

        for (let i = 0; i < importedData.length; i++) {
          const row = importedData[i];
          const progressPercent = 10 + Math.floor((i / totalRecords) * 90);
          setProgress(progressPercent);
          setProgressMessage(`Importing candidate ${i + 1} of ${totalRecords}`);
          setProgressDetails(`Success: ${successCount} | Failed: ${errorCount}`);

          try {
            const name = (row as any).name || (row as any).Name;
            const email = (row as any).email || (row as any).Email;
            const status = (row as any).status || (row as any).Status || "active";

            if (!name || !email) {
              errorCount++;
              errors.push(`Row ${i + 1}: Missing name or email`);
              continue;
            }

            const usersRef = collection(db, "users");
            const emailQuery = query(usersRef, where("email", "==", email));
            const emailSnapshot = await getDocs(emailQuery);

            if (!emailSnapshot.empty) {
              errorCount++;
              errors.push(`Row ${i + 1}: Email ${email} already exists`);
              continue;
            }

            const userCredential = await createUserWithEmailAndPassword(auth, email, DEFAULT_PASSWORD);

            const newCandidate = {
              email: email,
              name: name,
              role: "candidate",
              status: status.toLowerCase(),
              isActive: true,
              hasPassword: true,
              createdAt: new Date().toISOString(),
              updatedAt: new Date().toISOString(),
              authUid: userCredential.user.uid,
              orgId: orgIdToUse
            };

            await addDoc(usersRef, newCandidate);
            successCount++;
          } catch (err: any) {
            errorCount++;
            errors.push(`Row ${i + 1}: ${err.message}`);
          }

          if (i % 10 === 0) {
            await new Promise(resolve => setTimeout(resolve, 50));
          }
        }

        setProgress(100);
        setProgressMessage("Import completed! Refreshing data...");
        setProgressDetails("");

        await loadCandidates();

        setShowProgress(false);

        if (successCount > 0 && errorCount === 0) {
          await Swal.fire({
            icon: 'success',
            title: 'Import Successful!',
            html: `
              <div class="text-left">
                <p><strong>Total Records:</strong> ${totalRecords}</p>
                <p><strong>Successfully Imported:</strong> ${successCount}</p>
                <p><strong>Failed:</strong> ${errorCount}</p>
              </div>
            `,
            confirmButtonColor: '#6366f1',
            confirmButtonText: 'OK'
          });
        } else if (successCount > 0 && errorCount > 0) {
          await Swal.fire({
            icon: 'warning',
            title: 'Partial Import',
            html: `
              <div class="text-left">
                <p><strong>Total Records:</strong> ${totalRecords}</p>
                <p><strong>Successfully Imported:</strong> ${successCount}</p>
                <p><strong>Failed:</strong> ${errorCount}</p>
                ${errors.length > 0 && errors.length <= 3 ? `<hr/><p class="text-sm text-red-600">${errors.join('<br/>')}</p>` : ''}
                ${errors.length > 3 ? `<hr/><p class="text-sm text-red-600">${errors.slice(0, 3).join('<br/>')}<br/>...and ${errors.length - 3} more errors</p>` : ''}
              </div>
            `,
            confirmButtonColor: '#6366f1',
            confirmButtonText: 'OK'
          });
        } else {
          await Swal.fire({
            icon: 'error',
            title: 'Import Failed',
            html: `
              <div class="text-left">
                <p><strong>Total Records:</strong> ${totalRecords}</p>
                <p><strong>Successfully Imported:</strong> ${successCount}</p>
                <p><strong>Failed:</strong> ${errorCount}</p>
                <hr/>
                <p class="text-sm text-red-600">${errors.slice(0, 3).join('<br/>')}</p>
                ${errors.length > 3 ? `<p class="text-sm text-gray-500">...and ${errors.length - 3} more errors</p>` : ''}
              </div>
            `,
            confirmButtonColor: '#6366f1',
            confirmButtonText: 'OK'
          });
        }

        if (onCandidateChange) onCandidateChange();
      } catch (error: any) {
        console.error("Error importing file:", error);
        setShowProgress(false);
        await Swal.fire({
          icon: 'error',
          title: 'Import Error',
          text: error.message || "Error importing file. Please check the format.",
          confirmButtonColor: '#ef4444'
        });
      } finally {
        setImporting(false);
        event.target.value = "";
      }
    };

    reader.readAsArrayBuffer(file);
  };

  const handleResetPassword = async (candidate: Candidate) => {
    const result = await Swal.fire({
      title: 'Reset Password?',
      text: `Reset password for ${candidate.name} to "${DEFAULT_PASSWORD}"?`,
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: '#f59e0b',
      cancelButtonColor: '#6b7280',
      confirmButtonText: 'Yes, reset it!',
      cancelButtonText: 'Cancel'
    });

    if (!result.isConfirmed) return;

    setLoading(true);
    setLoadingMessage(`Resetting password for ${candidate.name}...`);

    try {
      if (candidate.authUid) {
        try {
          const userCredential = await signInWithEmailAndPassword(auth, candidate.email, DEFAULT_PASSWORD);
          await updatePassword(userCredential.user, DEFAULT_PASSWORD);
          await auth.signOut();
          await Swal.fire({
            icon: 'success',
            title: 'Password Reset!',
            text: `Password reset to ${DEFAULT_PASSWORD} for ${candidate.email}`,
            timer: 2000,
            showConfirmButton: false
          });
        } catch (error: any) {
          if (error.code === "auth/user-not-found") {
            setLoadingMessage("Recreating user account...");
            const userCredential = await createUserWithEmailAndPassword(auth, candidate.email, DEFAULT_PASSWORD);
            const usersRef = collection(db, "users");
            const q = query(usersRef, where("authUid", "==", candidate.authUid));
            const snapshot = await getDocs(q);
            if (!snapshot.empty) {
              const userDoc = snapshot.docs[0];
              await updateDoc(doc(db, "users", userDoc.id), {
                authUid: userCredential.user.uid,
                updatedAt: new Date().toISOString()
              });
            }
            await Swal.fire({
              icon: 'success',
              title: 'User Recreated!',
              text: `User recreated with password ${DEFAULT_PASSWORD}`,
              timer: 2000,
              showConfirmButton: false
            });
          } else {
            throw error;
          }
        }
      } else {
        setLoadingMessage("Creating authentication account...");
        const userCredential = await createUserWithEmailAndPassword(auth, candidate.email, DEFAULT_PASSWORD);
        const usersRef = collection(db, "users");
        const q = query(usersRef, where("email", "==", candidate.email));
        const snapshot = await getDocs(q);
        if (!snapshot.empty) {
          const userDoc = snapshot.docs[0];
          await updateDoc(doc(db, "users", userDoc.id), {
            authUid: userCredential.user.uid,
            updatedAt: new Date().toISOString()
          });
        }
        await Swal.fire({
          icon: 'success',
          title: 'Password Set!',
          text: `Password set to ${DEFAULT_PASSWORD} for ${candidate.email}`,
          timer: 2000,
          showConfirmButton: false
        });
      }
    } catch (error: any) {
      console.error("Error resetting password:", error);
      await Swal.fire({
        icon: 'error',
        title: 'Error',
        text: "Error resetting password: " + error.message,
        confirmButtonColor: '#ef4444'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSaveGroup = async () => {
    if (!groupForm.name.trim()) {
      await Swal.fire({
        icon: 'warning',
        title: 'Validation Error',
        text: 'Group name is required'
      });
      return;
    }

    setLoading(true);
    try {
      const orgIdToUse = orgId || currentOrgId;
      if (!orgIdToUse) throw new Error("No organisation selected");

      if (editGroup) {
        const groupRef = doc(db, "groups", editGroup.id);
        await updateDoc(groupRef, {
          name: groupForm.name,
          description: groupForm.description,
          updatedAt: new Date().toISOString()
        });
        await Swal.fire({
          icon: 'success',
          title: 'Updated!',
          text: 'Group updated successfully',
          timer: 1500,
          showConfirmButton: false
        });
      } else {
        const currentUser = auth.currentUser;
        const newGroup = {
          name: groupForm.name,
          description: groupForm.description,
          orgId: orgIdToUse,
          candidateIds: [],
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
          createdBy: currentUser?.uid || ""
        };
        await addDoc(collection(db, "groups"), newGroup);
        await Swal.fire({
          icon: 'success',
          title: 'Created!',
          text: 'Group created successfully',
          timer: 1500,
          showConfirmButton: false
        });
      }

      await loadGroups();
      setShowGroupModal(false);
      setGroupForm({ name: "", description: "" });
      setEditGroup(null);
    } catch (error: any) {
      await Swal.fire({
        icon: 'error',
        title: 'Error',
        text: error.message || "Failed to save group"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteGroup = async () => {
    if (!deleteGroup) return;

    setLoading(true);
    try {
      await deleteDoc(doc(db, "groups", deleteGroup.id));
      await loadGroups();
      setDeleteGroup(null);
      await Swal.fire({
        icon: 'success',
        title: 'Deleted!',
        text: 'Group deleted successfully',
        timer: 1500,
        showConfirmButton: false
      });
    } catch (error: any) {
      await Swal.fire({
        icon: 'error',
        title: 'Error',
        text: error.message || "Failed to delete group"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleViewGroupCandidates = async (group: Group) => {
    setSelectedGroup(group);
    setLoadingGroupCandidates(true);
    try {
      const candidatesList: Candidate[] = [];
      for (const candidateId of group.candidateIds || []) {
        const candidate = candidates.find(c => c.id === candidateId);
        if (candidate) {
          candidatesList.push(candidate);
        }
      }
      setGroupCandidates(candidatesList);
    } catch (error) {
      console.error("Error loading group candidates:", error);
    } finally {
      setLoadingGroupCandidates(false);
    }
  };

  const handleAddCandidatesToGroup = async () => {
    if (!selectedGroup || selectedCandidatesForGroup.size === 0) return;

    setLoading(true);
    try {
      const groupRef = doc(db, "groups", selectedGroup.id);
      const currentCandidateIds = selectedGroup.candidateIds || [];
      const combined = currentCandidateIds.concat(Array.from(selectedCandidatesForGroup));
      const newCandidateIds = Array.from(new Set(combined));

      await updateDoc(groupRef, {
        candidateIds: newCandidateIds,
        updatedAt: new Date().toISOString()
      });

      await loadGroups();
      await handleViewGroupCandidates({ ...selectedGroup, candidateIds: newCandidateIds });
      setSelectedCandidatesForGroup(new Set());
      setShowAddCandidatesModal(false);

      await Swal.fire({
        icon: 'success',
        title: 'Added!',
        text: `${selectedCandidatesForGroup.size} candidate(s) added to group`,
        timer: 1500,
        showConfirmButton: false
      });
    } catch (error: any) {
      await Swal.fire({
        icon: 'error',
        title: 'Error',
        text: error.message || "Failed to add candidates"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveCandidateFromGroup = async (candidateId: string) => {
    if (!selectedGroup) return;

    const result = await Swal.fire({
      title: 'Remove Candidate?',
      text: 'Are you sure you want to remove this candidate from the group?',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#ef4444',
      confirmButtonText: 'Yes, remove',
      cancelButtonText: 'Cancel'
    });

    if (!result.isConfirmed) return;

    setLoading(true);
    try {
      const groupRef = doc(db, "groups", selectedGroup.id);
      const newCandidateIds = (selectedGroup.candidateIds || []).filter(id => id !== candidateId);

      await updateDoc(groupRef, {
        candidateIds: newCandidateIds,
        updatedAt: new Date().toISOString()
      });

      await loadGroups();
      await handleViewGroupCandidates({ ...selectedGroup, candidateIds: newCandidateIds });

      await Swal.fire({
        icon: 'success',
        title: 'Removed!',
        text: 'Candidate removed from group',
        timer: 1500,
        showConfirmButton: false
      });
    } catch (error: any) {
      await Swal.fire({
        icon: 'error',
        title: 'Error',
        text: error.message || "Failed to remove candidate"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleBulkAddToGroup = async (group: Group) => {
    const availableCandidates = candidates.filter(c => !(group.candidateIds || []).includes(c.id));

    if (availableCandidates.length === 0) {
      await Swal.fire({
        icon: 'info',
        title: 'No Candidates',
        text: 'All candidates are already in this group',
        timer: 2000,
        showConfirmButton: false
      });
      return;
    }

    const { value: selectedCandidateIds } = await Swal.fire({
      title: `Add Candidates to ${group.name}`,
      html: `
        <div class="text-left">
          <p class="mb-3 text-sm text-gray-600">Select candidates to add to this group:</p>
          <div style="max-height: 400px; overflow-y: auto;">
            ${availableCandidates.map(c => `
              <label class="flex items-center gap-2 p-2 hover:bg-gray-50 rounded cursor-pointer">
                <input type="checkbox" value="${c.id}" class="candidate-checkbox">
                <div>
                  <p class="font-medium">${c.name}</p>
                  <p class="text-xs text-gray-500">${c.email}</p>
                </div>
              </label>
            `).join('')}
          </div>
          <div class="mt-3">
            <button type="button" id="select-all" class="text-sm text-indigo-600 hover:text-indigo-800">Select All</button>
            <span class="mx-2">|</span>
            <button type="button" id="clear-all" class="text-sm text-gray-500 hover:text-gray-700">Clear All</button>
          </div>
        </div>
      `,
      width: '600px',
      showCancelButton: true,
      confirmButtonText: 'Add Selected',
      cancelButtonText: 'Cancel',
      didOpen: () => {
        const selectAllBtn = document.getElementById('select-all');
        const clearAllBtn = document.getElementById('clear-all');

        if (selectAllBtn) {
          selectAllBtn.onclick = () => {
            document.querySelectorAll('.candidate-checkbox').forEach((cb: any) => {
              cb.checked = true;
            });
          };
        }

        if (clearAllBtn) {
          clearAllBtn.onclick = () => {
            document.querySelectorAll('.candidate-checkbox').forEach((cb: any) => {
              cb.checked = false;
            });
          };
        }
      },
      preConfirm: () => {
        const checkboxes = document.querySelectorAll('.candidate-checkbox:checked');
        const selected = Array.from(checkboxes).map((cb: any) => cb.value);

        if (selected.length === 0) {
          Swal.showValidationMessage('Please select at least one candidate');
          return false;
        }

        return selected;
      }
    });

    if (selectedCandidateIds) {
      setLoading(true);
      try {
        const groupRef = doc(db, "groups", group.id);
        const currentCandidateIds = group.candidateIds || [];
        const combined = currentCandidateIds.concat(Array.from(selectedCandidatesForGroup));
        const newCandidateIds = Array.from(new Set(combined));

        await updateDoc(groupRef, {
          candidateIds: newCandidateIds,
          updatedAt: new Date().toISOString()
        });

        await loadGroups();
        await Swal.fire({
          icon: 'success',
          title: 'Added!',
          text: `${selectedCandidateIds.length} candidate(s) added to ${group.name}`,
          timer: 2000,
          showConfirmButton: false
        });
      } catch (error: any) {
        await Swal.fire({
          icon: 'error',
          title: 'Error',
          text: error.message || "Failed to add candidates"
        });
      } finally {
        setLoading(false);
      }
    }
  };

  const filtered = candidates.filter(c =>
    c.name.toLowerCase().includes(search.toLowerCase()) ||
    c.email.toLowerCase().includes(search.toLowerCase())
  );

  const selectedFilteredCount = filtered.filter(c => selectedCandidates.has(c.id)).length;
  const isAllSelected = filtered.length > 0 && selectedFilteredCount === filtered.length;
  const isSomeSelected = selectedFilteredCount > 0 && selectedFilteredCount < filtered.length;

  const cols: Column<Candidate>[] = [
    {
      key: "select",
      label: "",
      render: (_, r) => (
        <input
          type="checkbox"
          checked={selectedCandidates.has(r.id)}
          onChange={() => handleSelectCandidate(r.id)}
          className="w-4 h-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500 cursor-pointer"
          disabled={bulkDeleting}
        />
      )
    },
    {
      key: "name",
      label: "Candidate",
      render: (v, r) => (
        <div className="flex items-center gap-2.5">
          <div className="w-9 h-9 rounded-full bg-gradient-to-br from-indigo-300 to-purple-400 flex items-center justify-center text-white font-bold text-sm flex-shrink-0">
            {r.name?.[0] || "?"}
          </div>
          <div>
            <p className="font-bold text-gray-800 text-sm">{v}</p>
            <p className="text-xs text-gray-400">{r.email}</p>
          </div>
        </div>
      )
    },
    { key: "status", label: "Status", render: v => <Badge text={v || "active"} /> },
    { key: "createdAt", label: "Joined", render: v => v ? new Date(v).toLocaleDateString() : "N/A" },
    {
      key: "id",
      label: "Actions",
      render: (_, r) => (
        <div className="flex gap-2">
          <button
            onClick={() => openEdit(r)}
            className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded hover:bg-blue-200 transition-colors"
            title="Edit Candidate"
            disabled={loading || bulkDeleting}
          >
            Edit
          </button>
          <button
            onClick={() => handleResetPassword(r)}
            className="text-xs bg-yellow-100 text-yellow-700 px-2 py-1 rounded hover:bg-yellow-200 transition-colors"
            title="Reset Password"
            disabled={loading || bulkDeleting}
          >
            Reset Pwd
          </button>
          <button
            onClick={() => setDeleteC(r)}
            className="text-xs bg-red-100 text-red-700 px-2 py-1 rounded hover:bg-red-200 transition-colors"
            title="Delete Candidate"
            disabled={loading || bulkDeleting}
          >
            Delete
          </button>
        </div>
      )
    }
  ];

  const groupColumns: Column<Group>[] = [
    {
      key: "name",
      label: "Group Name",
      render: (v, r) => (
        <div>
          <p className="font-bold text-gray-800">{v}</p>
          <p className="text-xs text-gray-500">{r.description || "No description"}</p>
        </div>
      )
    },
    {
      key: "candidateCount",
      label: "Members",
      render: (v) => <Badge text={`${v || 0} candidates`} variant="info" />
    },
    {
      key: "createdAt",
      label: "Created",
      render: (v) => new Date(v).toLocaleDateString()
    },
    {
      key: "id",
      label: "Actions",
      render: (_, r) => (
        <div className="flex gap-2">
          <button
            onClick={() => handleViewGroupCandidates(r)}
            className="text-xs bg-indigo-100 text-indigo-700 px-2 py-1 rounded hover:bg-indigo-200 transition-colors"
          >
            View Members
          </button>
          <button
            onClick={() => handleBulkAddToGroup(r)}
            className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded hover:bg-green-200 transition-colors"
          >
            Add Members
          </button>
          <button
            onClick={() => {
              setEditGroup(r);
              setGroupForm({ name: r.name, description: r.description || "" });
              setShowGroupModal(true);
            }}
            className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded hover:bg-blue-200 transition-colors"
          >
            Edit
          </button>
          <button
            onClick={() => setDeleteGroup(r)}
            className="text-xs bg-red-100 text-red-700 px-2 py-1 rounded hover:bg-red-200 transition-colors"
          >
            Delete
          </button>
        </div>
      )
    }
  ];

  const groupMemberColumns: Column<Candidate>[] = [
    {
      key: "name",
      label: "Candidate",
      render: (v, r) => (
        <div className="flex items-center gap-2.5">
          <div className="w-8 h-8 rounded-full bg-gradient-to-br from-indigo-300 to-purple-400 flex items-center justify-center text-white font-bold text-xs">
            {r.name?.[0] || "?"}
          </div>
          <div>
            <p className="font-medium text-gray-800 text-sm">{v}</p>
            <p className="text-xs text-gray-400">{r.email}</p>
          </div>
        </div>
      )
    },
    {
      key: "status",
      label: "Status",
      render: (v) => <Badge text={v || "active"} />
    },
    {
      key: "id",
      label: "Actions",
      render: (_, r) => (
        <button
          onClick={() => handleRemoveCandidateFromGroup(r.id)}
          className="text-xs bg-red-100 text-red-700 px-2 py-1 rounded hover:bg-red-200 transition-colors"
        >
          Remove
        </button>
      )
    }
  ];

  const totalCount = candidates.length;
  const activeCount = candidates.filter(c => c.status === "active").length;
  const inactiveCount = candidates.filter(c => c.status === "inactive").length;
  const pendingCount = candidates.filter(c => !c.hasPassword).length;

  return (
    <>
      {loading && !showProgress && (
        <LoadingOverlay message={loadingMessage} />
      )}

      <ProgressModal
        isOpen={showProgress}
        progress={progress}
        message={progressMessage}
        details={progressDetails}
      />

      <PageShell
        title={activeTab === "candidates" ? "Candidates" : "Groups"}
        subtitle={activeTab === "candidates"
          ? "Manage candidates in your organisation"
          : "Manage candidate groups for assessments"}
        action={activeTab === "candidates"
          ? { label: "Add Candidate", onClick: openNew, disabled: loading || importing || bulkDeleting }
          : undefined}
      >
        <div className="mb-6 border-b border-gray-200">
          <nav className="flex gap-6">
            <button
              onClick={() => setActiveTab("candidates")}
              className={`pb-3 px-1 font-medium text-sm transition-colors relative ${activeTab === "candidates"
                ? "text-indigo-600 border-b-2 border-indigo-600"
                : "text-gray-500 hover:text-gray-700"
                }`}
            >
              Candidates
              <span className="ml-2 px-2 py-0.5 bg-gray-100 text-gray-600 rounded-full text-xs">
                {candidates.length}
              </span>
            </button>
            <button
              onClick={() => setActiveTab("groups")}
              className={`pb-3 px-1 font-medium text-sm transition-colors relative ${activeTab === "groups"
                ? "text-indigo-600 border-b-2 border-indigo-600"
                : "text-gray-500 hover:text-gray-700"
                }`}
            >
              Groups
              <span className="ml-2 px-2 py-0.5 bg-gray-100 text-gray-600 rounded-full text-xs">
                {groups.length}
              </span>
            </button>
          </nav>
        </div>

        {activeTab === "candidates" && (
          <>
            <div className="grid grid-cols-4 gap-4 mb-6">
              <StatCard label="Total" value={totalCount} icon="👤" color="#6366f1" />
              <StatCard label="Active" value={activeCount} icon="✅" color="#10b981" />
              <StatCard label="Inactive" value={inactiveCount} icon="⛔" color="#ef4444" />
              <StatCard label="Pending" value={pendingCount} icon="⏳" color="#f59e0b" />
            </div>

            <GlassCard className="p-5">
              <div className="flex gap-3 mb-4 flex-wrap">
                <div className="flex-1 min-w-[200px]">
                  <Input
                    value={search}
                    onChange={setSearch}
                    placeholder="🔍 Search candidates by name or email..."
                    disabled={loading || importing || bulkDeleting}
                  />
                </div>
                <Button variant="secondary" onClick={downloadTemplate} disabled={loading || importing || bulkDeleting}>
                  📥 Download Template
                </Button>
                <label className="relative inline-block">
                  <input
                    type="file"
                    accept=".xlsx,.xls,.csv"
                    onChange={handleBulkImport}
                    className="absolute inset-0 opacity-0 cursor-pointer w-full"
                    disabled={importing || loading || bulkDeleting}
                  />
                  <Button variant="secondary" disabled={importing || loading || bulkDeleting}>
                    {importing ? "⏳ Importing..." : "📤 Bulk Import"}
                  </Button>
                </label>
                {selectedCandidates.size > 0 && (
                  <Button variant="danger" onClick={handleBulkDeleteSelected} disabled={bulkDeleting || loading}>
                    {bulkDeleting ? "🗑️ Deleting..." : `🗑️ Delete Selected (${selectedCandidates.size})`}
                  </Button>
                )}
              </div>

              {filtered.length > 0 && (
                <div className="mb-3 pb-2 border-b border-gray-200 flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={isAllSelected}
                    ref={(el) => {
                      if (el) el.indeterminate = isSomeSelected;
                    }}
                    onChange={handleSelectAll}
                    className="w-4 h-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500 cursor-pointer"
                    disabled={bulkDeleting}
                  />
                  <span className="text-sm font-medium text-gray-700">
                    Select All ({filtered.length} candidates)
                  </span>
                  {selectedCandidates.size > 0 && (
                    <span className="text-sm text-indigo-600 ml-2">
                      ({selectedCandidates.size} selected)
                    </span>
                  )}
                </div>
              )}

              {loading && !importing ? (
                <div className="text-center py-8">
                  <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600 mb-2"></div>
                  <p className="text-gray-500">{loadingMessage}</p>
                </div>
              ) : filtered.length === 0 ? (
                <div className="text-center py-8 text-gray-400">
                  {search ? "No candidates match your search" : "No candidates yet. Click 'Add Candidate' to get started."}
                </div>
              ) : (
                <Table
                  columns={cols}
                  data={filtered}
                  onEdit={openEdit}
                  onDelete={c => setDeleteC(c)}
                />
              )}
            </GlassCard>
          </>
        )}

        {activeTab === "groups" && (
          <div className="space-y-6">
            <div className="flex justify-between items-center">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Groups</h3>
                <p className="text-sm text-gray-500">Manage candidate groups for assessments</p>
              </div>
              <Button onClick={() => {
                setEditGroup(null);
                setGroupForm({ name: "", description: "" });
                setShowGroupModal(true);
              }} disabled={loading}>
                + Create Group
              </Button>
            </div>

            {groups.length === 0 ? (
              <GlassCard className="p-8 text-center">
                <p className="text-gray-400">No groups yet. Click "Create Group" to get started.</p>
              </GlassCard>
            ) : (
              <GlassCard className="p-5">
                <Table columns={groupColumns} data={groups} />
              </GlassCard>
            )}
          </div>
        )}
      </PageShell>

      <Modal isOpen={showModal} onClose={() => setShowModal(false)} title={editC ? "Edit Candidate" : "Add Candidate"} width="max-w-lg">
        <div className="space-y-4">
          <Input
            label="Full Name"
            value={form.name}
            onChange={F("name")}
            required
            placeholder="Enter full name"
            disabled={loading}
          />
          <Input
            label="Email"
            value={form.email}
            onChange={F("email")}
            type="email"
            required
            placeholder="Enter email address"
            disabled={!!editC || loading}
          />
          <Select
            label="Status"
            value={form.status}
            onChange={v => setForm(f => ({ ...f, status: v }))}
            options={[
              { value: "active", label: "Active" },
              { value: "inactive", label: "Inactive" }
            ]}
            disabled={loading}
          />
          {!editC && (
            <Input
              label="Password"
              value={form.password}
              onChange={F("password")}
              type="password"
              placeholder={`Default: ${DEFAULT_PASSWORD}`}
              helperText={`Leave as "${DEFAULT_PASSWORD}" or set custom password`}
              disabled={loading}
            />
          )}
          {editC && (
            <Input
              label="New Password (optional)"
              value={form.password}
              onChange={F("password")}
              type="password"
              placeholder="Leave blank to keep current password"
              helperText="Enter new password only if you want to change it"
              disabled={loading}
            />
          )}
          <div className="flex gap-3 justify-end pt-2 border-t border-gray-100">
            <Button variant="secondary" onClick={() => setShowModal(false)} disabled={loading}>
              Cancel
            </Button>
            <Button onClick={save} disabled={loading}>
              {loading ? "Saving..." : (editC ? "Update" : "Add")}
            </Button>
          </div>
        </div>
      </Modal>

      <Modal
        isOpen={showGroupModal}
        onClose={() => {
          setShowGroupModal(false);
          setEditGroup(null);
          setGroupForm({ name: "", description: "" });
        }}
        title={editGroup ? "Edit Group" : "Create Group"}
        width="max-w-lg"
      >
        <div className="space-y-4">
          <Input
            label="Group Name"
            value={groupForm.name}
            onChange={(v) => setGroupForm({ ...groupForm, name: v })}
            placeholder="e.g., Frontend Developers, Senior Candidates"
            required
          />
          <Textarea
            label="Description"
            value={groupForm.description}
            onChange={(v) => setGroupForm({ ...groupForm, description: v })}
            placeholder="Optional description for this group"
            rows={3}
          />
          <div className="flex gap-3 justify-end pt-2">
            <Button variant="secondary" onClick={() => setShowGroupModal(false)}>
              Cancel
            </Button>
            <Button onClick={handleSaveGroup}>
              {editGroup ? "Update" : "Create"}
            </Button>
          </div>
        </div>
      </Modal>

      <Modal
        isOpen={!!selectedGroup}
        onClose={() => {
          setSelectedGroup(null);
          setGroupCandidates([]);
        }}
        title={`Group Members: ${selectedGroup?.name}`}
        width="max-w-2xl"
      >
        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <p className="text-sm text-gray-600">
              Total Members: {groupCandidates.length}
            </p>
            <Button
              variant="secondary"
              size="sm"
              onClick={() => setShowAddCandidatesModal(true)}
              disabled={loading || loadingGroupCandidates}
            >
              + Add Members
            </Button>
          </div>

          {loadingGroupCandidates ? (
            <div className="text-center py-8">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
            </div>
          ) : groupCandidates.length === 0 ? (
            <div className="text-center py-8 text-gray-400">
              No members in this group yet. Click "Add Members" to add candidates.
            </div>
          ) : (
            <Table columns={groupMemberColumns} data={groupCandidates} />
          )}
        </div>
      </Modal>

      <Modal
        isOpen={showAddCandidatesModal}
        onClose={() => {
          setShowAddCandidatesModal(false);
          setSelectedCandidatesForGroup(new Set());
        }}
        title={`Add Candidates to ${selectedGroup?.name}`}
        width="max-w-2xl"
      >
        <div className="space-y-4">
          <div className="border rounded-lg p-4 max-h-96 overflow-y-auto">
            {candidates
              .filter(c => !(selectedGroup?.candidateIds || []).includes(c.id))
              .map(candidate => (
                <label key={candidate.id} className="flex items-center gap-3 p-3 hover:bg-gray-50 rounded-lg cursor-pointer transition-colors">
                  <input
                    type="checkbox"
                    checked={selectedCandidatesForGroup.has(candidate.id)}
                    onChange={() => {
                      const newSet = new Set(selectedCandidatesForGroup);
                      if (newSet.has(candidate.id)) {
                        newSet.delete(candidate.id);
                      } else {
                        newSet.add(candidate.id);
                      }
                      setSelectedCandidatesForGroup(newSet);
                    }}
                    className="w-4 h-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                  />
                  <div className="flex-1">
                    <p className="font-medium text-gray-800">{candidate.name}</p>
                    <p className="text-xs text-gray-500">{candidate.email}</p>
                  </div>
                  <Badge text={candidate.status || "active"} />
                </label>
              ))}
            {candidates.filter(c => !(selectedGroup?.candidateIds || []).includes(c.id)).length === 0 && (
              <p className="text-center text-gray-400 py-4">No candidates available to add</p>
            )}
          </div>
          <div className="flex justify-between items-center pt-2">
            <div className="text-sm text-gray-600">
              {selectedCandidatesForGroup.size} candidate(s) selected
            </div>
            <div className="flex gap-3">
              <Button variant="secondary" onClick={() => setShowAddCandidatesModal(false)}>
                Cancel
              </Button>
              <Button onClick={handleAddCandidatesToGroup} disabled={selectedCandidatesForGroup.size === 0}>
                Add Selected
              </Button>
            </div>
          </div>
        </div>
      </Modal>

      <ConfirmDialog
        isOpen={!!deleteC}
        onClose={() => setDeleteC(null)}
        onConfirm={handleSingleDelete}
        title="Remove Candidate"
        message={`Remove "${deleteC?.name}" from this organisation? This will also delete their authentication account.`}
        confirmLabel="Remove"
      />

      <ConfirmDialog
        isOpen={!!deleteGroup}
        onClose={() => setDeleteGroup(null)}
        onConfirm={handleDeleteGroup}
        title="Delete Group"
        message={`Are you sure you want to delete "${deleteGroup?.name}"? This will not delete the candidates, only remove the group association.`}
        confirmLabel="Delete Group"
      />
    </>
  );
};

export default CandidatesPage;